import type { AccountQueryResponse, AccountRowDto, ProjectSummary } from '@shared/api';
import { defaultExportColumns, type ExportSummary } from '@shared/exports';
import { countConditions, emptyFilter, type FieldDescriptor, type FilterGroup, type SortSpec } from '@shared/filters';
import type { SavedViewConfig } from '@shared/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Building2, Download, ExternalLink, Mail, Phone, Search, Tag, Trash2, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { Checkbox, Input, Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';
import { AccountDrawer } from './account-drawer';
import { AssistantPanel } from './assistant-panel';
import { FilterBar } from './filter-bar';
import { SavedViews, toSavedViewConfig } from './saved-views';

const PAGE_SIZE = 50;

type StatusFilter = 'active' | 'discarded' | 'all';

/**
 * Where an export goes. A file download and a spreadsheet write take different arguments, so the
 * target carries them rather than the component tracking a format plus loose sheet fields.
 */
type ExportTarget = { format: 'csv' | 'xlsx' } | { format: 'google_sheets'; spreadsheet: string; tabName: string };
interface SortState {
  field: string;
  dir: 'asc' | 'desc';
}

interface Column {
  id: string;
  label: string;
  sortable: boolean;
  align?: 'right';
  render: (row: AccountRowDto) => React.ReactNode;
}

/** Core columns, in display order. Ids match the server's field whitelist. */
function buildColumns(onOpen: (id: string) => void): Column[] {
  return [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (row) => (
        <button
          type="button"
          className="truncate text-left font-medium text-foreground hover:text-accent hover:underline"
          onClick={() => onOpen(row.id)}
        >
          {row.name}
        </button>
      ),
    },
    { id: 'category', label: 'Category', sortable: true, render: (row) => row.category ?? '' },
    { id: 'city', label: 'City', sortable: true, render: (row) => [row.area, row.city].filter(Boolean).join(', ') },
    {
      id: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => (row.phone ? <span className="tabular">{row.phone}</span> : <span className="text-muted-foreground">—</span>),
    },
    {
      id: 'email',
      label: 'Email',
      sortable: true,
      render: (row) => (row.email ? <span className="truncate">{row.email}</span> : <span className="text-muted-foreground">—</span>),
    },
    {
      id: 'website',
      label: 'Website',
      sortable: true,
      render: (row) =>
        row.website ? (
          <a className="truncate text-accent underline-offset-4 hover:underline" href={row.website} target="_blank" rel="noreferrer noopener">
            {row.domain ?? row.website}
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'rating',
      label: 'Rating',
      sortable: true,
      align: 'right',
      render: (row) => (row.rating === null ? '' : <span className="tabular">{row.rating.toFixed(1)}</span>),
    },
    {
      id: 'reviewCount',
      label: 'Reviews',
      sortable: true,
      align: 'right',
      render: (row) => (row.reviewCount === null ? '' : <span className="tabular">{row.reviewCount.toLocaleString()}</span>),
    },
    {
      id: 'sourceCount',
      label: 'Sources',
      sortable: true,
      align: 'right',
      render: (row) => (
        <span className="tabular" title={row.sources.join(', ')}>
          {row.sourceCount}
        </span>
      ),
    },
    {
      id: 'completeness',
      label: 'Complete',
      sortable: true,
      align: 'right',
      render: (row) => (row.completeness === null ? '' : <span className="tabular">{Math.round(row.completeness * 100)}%</span>),
    },
  ];
}

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function cellValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toLocaleString();
  return JSON.stringify(value);
}

/** The results workspace: every business the research kept, filterable and editable in bulk. */
export function AccountsPage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') ?? undefined;
  const client = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [sort, setSort] = useState<SortState>({ field: 'reviewCount', dir: 'desc' });
  const [filter, setFilter] = useState<FilterGroup>(() => emptyFilter());
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tagValue, setTagValue] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [sheetFormOpen, setSheetFormOpen] = useState(false);
  const [spreadsheet, setSpreadsheet] = useState('');
  const [tabName, setTabName] = useState('Businesses');
  const debouncedSearch = useDebounced(search);
  const debouncedFilter = useDebounced(filter, 400);

  const columns = useMemo(() => buildColumns(setOpenId), [setOpenId]);

  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: () => api.get<ProjectSummary[]>('/api/projects') });

  // Columns and filterable fields both come from the project's own configuration, so a project
  // that collected custom attributes exposes them here without any code change.
  const fieldsQuery = useQuery({
    queryKey: queryKeys.accountFields(projectId),
    queryFn: () =>
      api.get<FieldDescriptor[]>(`/api/accounts/fields${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`),
  });

  const allFields = useMemo(() => fieldsQuery.data ?? [], [fieldsQuery.data]);

  // Provenance attributes (meta.*) are shown in the detail view, not as table columns.
  const attributeFields = useMemo(
    () => allFields.filter((field) => field.attribute !== undefined && !field.attribute.startsWith('meta.')).slice(0, 8),
    [allFields],
  );

  const sortableFields = useMemo(() => allFields.filter((field) => field.sortable), [allFields]);

  const filterActive = countConditions(debouncedFilter) > 0;

  const body = useMemo(
    () => ({
      ...(projectId ? { projectId } : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(filterActive ? { filter: debouncedFilter } : {}),
      status,
      sort: [{ field: sort.field, dir: sort.dir }],
      offset,
      limit: PAGE_SIZE,
      ...(attributeFields.length > 0 ? { fields: attributeFields.map((field) => field.id) } : {}),
    }),
    [projectId, debouncedSearch, debouncedFilter, filterActive, status, sort, offset, attributeFields],
  );

  const results = useQuery({
    queryKey: queryKeys.accounts(body),
    queryFn: () => api.post<AccountQueryResponse>('/api/accounts/query', body),
  });

  const bulk = useMutation({
    mutationFn: (input: { action: 'delete' | 'restore' | 'discard' | 'tag' | 'untag'; tag?: string }) =>
      api.post<{ ok: true; affected: number }>('/api/accounts/bulk', {
        ids: [...selected],
        action: input.action,
        ...(input.tag ? { tag: input.tag } : {}),
      }),
    onSuccess: async () => {
      setSelected(new Set());
      setTagValue('');
      await client.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  /**
   * Export what is on screen: the same project, search, filter, status and ordering. Selecting
   * rows narrows it to just those, so "export my selection" needs no separate mode.
   */
  const exportRows = useMutation({
    mutationFn: (target: ExportTarget) =>
      api.post<ExportSummary>('/api/exports', {
        format: target.format,
        scope: {
          ...(projectId ? { projectId } : {}),
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
          ...(filterActive ? { filter: debouncedFilter } : {}),
          ...(selected.size > 0 ? { ids: [...selected] } : {}),
          sort: [{ field: sort.field, dir: sort.dir }],
          status,
        },
        columns: defaultExportColumns(),
        fileName: activeProjectName ?? 'businesses',
        ...(target.format === 'google_sheets'
          ? { sheet: { spreadsheet: target.spreadsheet, tabName: target.tabName } }
          : {}),
      }),
    onSuccess: (summary) => {
      if (summary.sheetUrl) {
        setSheetFormOpen(false);
        window.open(summary.sheetUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      // The browser follows the download URL; the file itself is never held in memory here.
      if (summary.downloadUrl) window.location.assign(summary.downloadUrl);
    },
  });

  const rows = results.data?.items ?? [];
  const total = results.data?.total ?? 0;
  const allOnPageSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  const toggleSort = (field: string): void => {
    setOffset(0);
    setSort((current) => (current.field === field ? { field, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }));
  };

  const toggleRow = (id: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const activeProject = projects.data?.find((project) => project.id === projectId);
  const activeProjectName = activeProject?.name;

  /** Restore a saved view: filter, search, ordering and status move together or not at all. */
  const applySavedView = (config: SavedViewConfig): void => {
    setFilter(config.filter ?? emptyFilter());
    setSearch(config.search ?? '');
    const first = config.sort[0];
    if (first) setSort({ field: first.field, dir: first.dir });
    setStatus(config.status ?? 'active');
    setOffset(0);
    setSelected(new Set());
  };

  const applyAssistantFilter = (next: FilterGroup, nextSort: SortSpec[] | undefined): void => {
    setFilter(next);
    const first = nextSort?.[0];
    if (first) setSort({ field: first.field, dir: first.dir });
    setOffset(0);
    setSelected(new Set());
  };

  return (
    <div className="mx-auto max-w-[1600px] px-5 py-5">
      <header className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Businesses</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {total > 0 ? (
            <>
              <span className="tabular">{total.toLocaleString()}</span> businesses discovered in the selected sources and queries —
              a market observation, not a complete census.
            </>
          ) : (
            'Results from your research runs.'
          )}
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            className="pl-8"
            value={search}
            placeholder="Search name, phone, email, website"
            aria-label="Search businesses"
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        </div>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Project</span>
          <Select
            className="h-9 w-48"
            value={projectId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParams(value ? { project: value } : {});
              setOffset(0);
            }}
          >
            <option value="">All projects</option>
            {projects.data?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Status</span>
          <Select
            className="h-9 w-32"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as StatusFilter);
              setOffset(0);
            }}
          >
            <option value="active">Kept</option>
            <option value="discarded">Discarded</option>
            <option value="all">All</option>
          </Select>
        </label>

        {/* Sorting is not limited to the visible columns — any sortable field, including
            collected attributes and custom ones, can order the table. */}
        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Sort by</span>
          <Select
            className="h-9 w-48"
            value={sort.field}
            onChange={(event) => {
              setSort((current) => ({ field: event.target.value, dir: current.dir }));
              setOffset(0);
            }}
          >
            {sortableFields.map((field) => (
              <option key={field.id} value={field.id}>
                {field.label}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="outline"
            aria-label={sort.dir === 'asc' ? 'Sort descending' : 'Sort ascending'}
            icon={sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
            onClick={() => setSort((current) => ({ ...current, dir: current.dir === 'asc' ? 'desc' : 'asc' }))}
          >
            {sort.dir === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </label>
      </div>

      <div className="mb-3 space-y-2 rounded-[var(--radius-panel)] border border-border bg-surface p-3">
        <AssistantPanel projectId={projectId} onApply={applyAssistantFilter} />
        <SavedViews
          projectId={projectId}
          current={toSavedViewConfig({
            filter: filterActive ? debouncedFilter : undefined,
            search,
            sort: [{ field: sort.field, dir: sort.dir }],
            status,
          })}
          onApply={applySavedView}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <FilterBar
          fields={allFields}
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setOffset(0);
          }}
        />
        <span className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            icon={<Download className="h-3.5 w-3.5" />}
            loading={exportRows.isPending && exportRows.variables?.format === 'csv'}
            disabled={total === 0 || exportRows.isPending}
            onClick={() => exportRows.mutate({ format: 'csv' })}
          >
            {selected.size > 0 ? `Export ${selected.size} as CSV` : 'Export CSV'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            icon={<Download className="h-3.5 w-3.5" />}
            loading={exportRows.isPending && exportRows.variables?.format === 'xlsx'}
            disabled={total === 0 || exportRows.isPending}
            onClick={() => exportRows.mutate({ format: 'xlsx' })}
          >
            Excel
          </Button>
          <Button
            size="sm"
            variant={sheetFormOpen ? 'accent' : 'outline'}
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            disabled={total === 0}
            onClick={() => setSheetFormOpen((current) => !current)}
          >
            Google Sheets
          </Button>
        </span>
      </div>

      {sheetFormOpen ? (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-[var(--radius-panel)] border border-border bg-surface p-3">
          <label className="flex min-w-64 flex-1 flex-col gap-1">
            <span className="text-[13px] font-medium text-foreground">Google Sheet link</span>
            <Input
              value={spreadsheet}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              aria-label="Google Sheet link"
              onChange={(event) => setSpreadsheet(event.target.value)}
            />
          </label>
          <label className="flex w-40 flex-col gap-1">
            <span className="text-[13px] font-medium text-foreground">Tab</span>
            <Input value={tabName} aria-label="Tab name" onChange={(event) => setTabName(event.target.value)} />
          </label>
          <Button
            size="sm"
            className="h-9"
            loading={exportRows.isPending && exportRows.variables?.format === 'google_sheets'}
            disabled={!spreadsheet.trim() || !tabName.trim() || exportRows.isPending}
            onClick={() =>
              exportRows.mutate({ format: 'google_sheets', spreadsheet: spreadsheet.trim(), tabName: tabName.trim() })
            }
          >
            Write to sheet
          </Button>
          <p className="w-full text-[13px] text-muted-foreground">
            The tab is replaced with these {total.toLocaleString()} rows. Share the sheet with your service account&apos;s
            email address (Editor access) first — connect it on the{' '}
            <Link className="text-accent underline-offset-4 hover:underline" to="/sources">
              Sources page
            </Link>
            .
          </p>
        </div>
      ) : null}

      {exportRows.error ? (
        <Alert className="mb-2" tone="error">
          {errorMessage(exportRows.error)}
        </Alert>
      ) : null}

      {activeProject?.stats && status === 'active' && !filterActive ? (
        <p className="mb-3 text-[13px] text-muted-foreground">
          {activeProject.name}: <span className="tabular">{activeProject.stats.accounts.toLocaleString()}</span> discovered,{' '}
          <span className="tabular">{activeProject.stats.contactable.toLocaleString()}</span> with usable contact details.
        </p>
      ) : null}

      {selected.size > 0 ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-[var(--radius-panel)] border border-accent bg-accent-subtle px-3 py-2">
          <span className="tabular text-[13px] font-medium text-accent">{selected.size} selected</span>
          <span className="flex-1" />
          <Input
            className="h-7 w-40"
            value={tagValue}
            placeholder="Tag name"
            aria-label="Tag name"
            onChange={(event) => setTagValue(event.target.value)}
          />
          <Button
            size="sm"
            variant="outline"
            icon={<Tag className="h-3.5 w-3.5" />}
            disabled={!tagValue.trim() || bulk.isPending}
            onClick={() => bulk.mutate({ action: 'tag', tag: tagValue.trim() })}
          >
            Tag
          </Button>
          {status === 'discarded' ? (
            <Button size="sm" variant="outline" icon={<Undo2 className="h-3.5 w-3.5" />} onClick={() => bulk.mutate({ action: 'restore' })}>
              Restore
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => bulk.mutate({ action: 'discard' })}>
              Discard
            </Button>
          )}
          <Button size="sm" variant="destructive" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => bulk.mutate({ action: 'delete' })}>
            Delete
          </Button>
          <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      {bulk.error ? (
        <Alert className="mb-2" tone="error">
          {errorMessage(bulk.error)}
        </Alert>
      ) : null}

      <Card className="overflow-hidden">
        {results.isPending ? (
          <div className="flex items-center gap-2 px-4 py-10 text-sm text-muted-foreground">
            <Spinner /> Loading businesses…
          </div>
        ) : results.isError ? (
          <Alert tone="error" className="m-3">
            {errorMessage(results.error)}
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title={debouncedSearch || filterActive ? 'No businesses match these filters' : 'No businesses yet'}
            description={
              debouncedSearch || filterActive
                ? 'Try loosening a condition, or clear the filters to see everything.'
                : 'Run a research project to collect businesses, then review them here.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[length:var(--text-table)]">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th scope="col" className="w-9 px-3 py-2">
                    <Checkbox
                      checked={allOnPageSelected}
                      aria-label={allOnPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
                      onChange={(event) =>
                        setSelected((current) => {
                          const next = new Set(current);
                          for (const row of rows) {
                            if (event.target.checked) next.add(row.id);
                            else next.delete(row.id);
                          }
                          return next;
                        })
                      }
                    />
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.id}
                      scope="col"
                      className={cn('whitespace-nowrap px-3 py-2 text-left font-medium text-muted-foreground', column.align === 'right' && 'text-right')}
                      aria-sort={sort.field === column.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {column.sortable ? (
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(column.id)}>
                          {column.label}
                          {sort.field === column.id ? (
                            sort.dir === 'asc' ? (
                              <ArrowUp className="h-3 w-3" aria-hidden="true" />
                            ) : (
                              <ArrowDown className="h-3 w-3" aria-hidden="true" />
                            )
                          ) : null}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                  {attributeFields.map((field) => (
                    <th key={field.id} scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium text-muted-foreground">
                      {field.label}
                    </th>
                  ))}
                  <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium text-muted-foreground">
                    Contact
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-border last:border-b-0 transition-colors duration-150 hover:bg-muted/50',
                      selected.has(row.id) && 'bg-accent-subtle',
                      row.status === 'discarded' && 'opacity-60',
                    )}
                  >
                    <td className="px-3 py-1.5">
                      <Checkbox checked={selected.has(row.id)} aria-label={`Select ${row.name}`} onChange={() => toggleRow(row.id)} />
                    </td>
                    {columns.map((column) => (
                      <td key={column.id} className={cn('max-w-64 truncate px-3 py-1.5 text-foreground', column.align === 'right' && 'text-right')}>
                        {column.render(row)}
                      </td>
                    ))}
                    {attributeFields.map((field) => (
                      <td key={field.id} className="max-w-48 truncate px-3 py-1.5 text-foreground">
                        {cellValue(field.attribute ? row.attributes[field.attribute] : null)}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span className="flex items-center gap-1">
                        {row.hasPhone ? <Phone className="h-3.5 w-3.5 text-success" aria-label="Has phone" /> : null}
                        {row.hasEmail ? <Mail className="h-3.5 w-3.5 text-success" aria-label="Has email" /> : null}
                        {row.discardReason ? <Badge tone="neutral">{row.discardReason.replace(/_/g, ' ')}</Badge> : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > PAGE_SIZE ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="tabular text-[13px] text-muted-foreground">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
          </p>
          <span className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </span>
        </div>
      ) : null}

      {openId ? <AccountDrawer id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}
